import { AxiosResponse } from 'axios';
import { Animal, InventoryGraphicsData, SpecimenInventory, SpecimenLocation } from '../types/interfaces';
import { Genders } from '../types/enums/genders.enum';
import { activeContexts } from '../types/enums/activeContexts.enum';
import { QualityType } from '../types/enums/qualityType.enum';
import { specimenUOM } from '../types/enums/specimenUOM.enum';
import * as onFarmAnimalsApi from '../api/onFarmAnimalsApi';
import * as onFarmSpecimenApi from '../api/onFarmSpecimenApi';
import { getOnFarmCanisterInventory } from '../api/onFarmTransactionApi';
import { CreateOnFarmAnimalRequest, OnFarmAnimal, UpdateOnFarmAnimalRequest } from '../types/interfaces/animal.interfaces';
import { OnFarmEmbryoStraw, OnFarmSpecimen, OnFarmSpecimenLocation } from '../types/interfaces/onFarmSpecimen.interfaces';
import { EmbryoStraw } from '../types/interfaces/specimen.interfaces';
import { PagedResponse, StandardParams } from '../types/interfaces/apiParams.interfaces';

/**
 * OnFarm Inventory service
 *
 * Animals and specimen inventory use the OnFarm APIs.
 *
 * Endpoint mapping:
 * - getOnFarmAccountAnimals           -> GET /onfarmanimal
 * - getOnFarmAnimalById               -> GET /onfarmanimal/{id}
 * - createOnFarmAnimal                -> POST /onfarmanimal
 * - updateOnFarmAnimal                -> PUT /onfarmanimal/{id}
 * - getOnFarmSpecimensInventoryByCanisterId -> GET /OnFarmTransaction/canister-inventory
 * - getOnFarmSpecimensByOwnership           -> GET /OnFarmSpecimen/animals/{animalId}
 * - getOnFarmTotalUnits                     -> GET /OnFarmSpecimen
 * - getOnFarmInventoryGraphicsData          -> GET /OnFarmSpecimen/accounts/{accountId}
 */

const mapOnFarmAnimal = (animal: OnFarmAnimal): Animal => ({
  ...animal,
  animalId: animal.animalId ?? animal.onFarmAnimalId,
  name: animal.name ?? '',
  code: animal.code ?? '',
});

export const toPagedOnFarmAnimals = (
  data: OnFarmAnimal[] | PagedResponse<OnFarmAnimal> | null | undefined,
): PagedResponse<Animal> => {
  if (Array.isArray(data)) {
    const result = data.map(mapOnFarmAnimal);
    return { result, total: result.length, limit: result.length, offset: 0 };
  }

  const result = (data?.result ?? []).map(mapOnFarmAnimal);
  return {
    result,
    total: data?.total ?? result.length,
    limit: data?.limit ?? result.length,
    offset: data?.offset ?? 0,
  };
};

const mapOnFarmStorageCanister = (
  canister: OnFarmSpecimenLocation['accountStorageCanister'],
): SpecimenLocation['storageCanister'] => {
  if (!canister) {
    return undefined;
  }

  const tank = canister.accountStorageTank;
  const site = tank?.accountStorageSite;

  return {
    storageCanisterId: canister.accountStorageCanisterId,
    name: canister.name,
    active: canister.active,
    storageTankId: canister.accountStorageTankId,
    storageTank: tank
      ? {
          storageTankId: tank.accountStorageTankId,
          name: tank.name,
          active: tank.active,
          isTravelingTank: tank.isTravelingTank,
          storageSiteId: tank.accountStorageSiteId,
          storageSite: site
            ? {
                storageSiteId: site.accountStorageSiteId,
                name: site.name,
                active: site.active,
              }
            : undefined,
        }
      : undefined,
  };
};

const mapOnFarmSpecimenLocation = (location: OnFarmSpecimenLocation): SpecimenLocation => ({
  specimenLocationId: location.onFarmSpecimenLocationId,
  specimenId: location.onFarmSpecimenId,
  quantity: location.quantity ?? 0,
  storageCanisterId: location.accountStorageCanisterId,
  storageCanister: mapOnFarmStorageCanister(location.accountStorageCanister),
  transactionLinkId: location.transactionLinkId ?? 0,
});

const mapOnFarmSpecimenToInventory = (specimen: OnFarmSpecimen): SpecimenInventory => {
  const animal = specimen.onFarmAnimal;
  const locations = (specimen.onFarmSpecimenLocations ?? []).map(mapOnFarmSpecimenLocation);
  const quantityAvailable = locations.reduce((total, location) => total + location.quantity, 0);
  const embryoStraws = (specimen.onFarmEmbryoStraws ?? [])
    .filter(straw => straw.consumedByOnFarmTransactionId == null)
    .map(straw => mapOnFarmEmbryoStraw(straw, specimen.onFarmSpecimenId, straw.accountStorageCanisterId ?? 0));

  return {
    specimenId: specimen.onFarmSpecimenId,
    animalId: specimen.onFarmAnimalId ?? animal?.onFarmAnimalId ?? animal?.animalId ?? 0,
    animal: animal ? mapOnFarmAnimal(animal) : undefined,
    collectedBy: specimen.collectedBy ?? 0,
    freezeDate: specimen.freezeDate ? new Date(specimen.freezeDate) : new Date(0),
    frozenBy: specimen.frozenBy ?? 0,
    originalQuantity: specimen.originalQuantity ?? quantityAvailable,
    processedBy: specimen.processedBy ?? 0,
    quality: (specimen.quality as QualityType) ?? QualityType.Unknown,
    qualityPercentage: specimen.qualityPercentage ?? 0,
    specimenTypeId: specimen.specimenTypeId ?? specimen.specimenType?.specimenTypeId ?? 0,
    specimenUOM: (specimen.specimenUOM as specimenUOM) ?? specimenUOM.Default,
    lotNumber: specimen.lotNumber,
    notes: specimen.notes ?? undefined,
    embryologistName: specimen.embryologistName ?? undefined,
    technicianName: specimen.technicianName ?? undefined,
    veterinarianName: specimen.veterinarianName ?? undefined,
    frozenAtSiteId: specimen.frozenAtSiteId ?? undefined,
    frozenAtSite: specimen.frozenAtSite as SpecimenInventory['frozenAtSite'],
    specimenType: specimen.specimenType ?? undefined,
    specimenCustomDataValues: (specimen.onFarmSpecimenCustomDataValues ?? []).map(value => ({
      specimenId: specimen.onFarmSpecimenId,
      specimenCustomDataKeyId: value.specimenCustomDataKeyId,
      specimenCustomDataKey: value.specimenCustomDataKey
        ? {
            specimenCustomDataKeyId: value.specimenCustomDataKey.specimenCustomDataKeyId,
            specimenTypeId: specimen.specimenTypeId ?? 0,
            keyName: value.specimenCustomDataKey.keyName,
            required: value.specimenCustomDataKey.required ?? false,
            isNumeric: value.specimenCustomDataKey.isNumeric ?? false,
            minValue: value.specimenCustomDataKey.minValue,
            maxValue: value.specimenCustomDataKey.maxValue,
            active: value.specimenCustomDataKey.active ?? true,
          }
        : undefined,
      value: value.value ?? undefined,
    })),
    specimenLocations: locations,
    embryoStraws,
    quantityAvailable,
    quantityOwned: quantityAvailable,
    embryoStrawId: 0,
    embryoCountPerStraw: 0,
  };
};

export const getOnFarmAccountAnimals = async (params?: StandardParams): Promise<AxiosResponse<PagedResponse<Animal>>> => {
  const response = await onFarmAnimalsApi.getOnFarmAccountAnimals(params);
  return {
    ...response,
    data: toPagedOnFarmAnimals(response.data),
  };
};

export const createOnFarmAnimal = (animal: CreateOnFarmAnimalRequest): Promise<AxiosResponse<number>> => {
  return onFarmAnimalsApi.createOnFarmAnimal(animal);
};

export const getOnFarmAnimalById = async (
  animalId: number | string,
  params?: { include?: string },
): Promise<AxiosResponse<Animal>> => {
  const resolvedAnimalId = Number(animalId);
  if (!resolvedAnimalId) {
    throw new Error('Animal id is required');
  }

  const response = await onFarmAnimalsApi.getOnFarmAnimalById(resolvedAnimalId, params);
  return {
    ...response,
    data: mapOnFarmAnimal(response.data),
  };
};

/** PUT /onfarmanimal/{id} */
export const updateOnFarmAnimal = (
  animalId: number | string,
  animal: UpdateOnFarmAnimalRequest,
): Promise<AxiosResponse<OnFarmAnimal>> => {
  return onFarmAnimalsApi.updateOnFarmAnimal(animalId, animal);
};

export const getOnFarmSpecimensByOwnership = async (
  animalId: number | string,
  _accountId?: number | string,
  params?: StandardParams,
): Promise<AxiosResponse<SpecimenInventory[]>> => {
  const response = await onFarmSpecimenApi.getOnFarmSpecimensByAnimalId(animalId, {
    ...params,
    include:
      'OnFarmAnimal,SpecimenType,FrozenAtSite,OnFarmSpecimenLocations.AccountStorageCanister.AccountStorageTank.AccountStorageSite,OnFarmEmbryoStraws,OnFarmSpecimenCustomDataValues.SpecimenCustomDataKey',
  });

  return { ...response, data: (response.data ?? []).map(mapOnFarmSpecimenToInventory) };
};

/**
 * GET /OnFarmTransaction/canister-inventory
 * Maps OnFarm location inventory into the SpecimenInventory shape used by SelectSpecimenFromCanisterTable.
 */
export const getOnFarmSpecimensInventoryByCanisterId = async (
  canisterId: number | string,
  _accountId?: number,
): Promise<AxiosResponse<SpecimenInventory[]>> => {
  const targetCanisterId = Number(canisterId);
  const response = await getOnFarmCanisterInventory(targetCanisterId);
  return {
    ...response,
    data: (response.data ?? []).map(location => mapOnFarmLocationToSpecimenInventory(location, targetCanisterId)),
  };
};

const mapOnFarmEmbryoStraw = (straw: OnFarmEmbryoStraw, onFarmSpecimenId: number, canisterId: number): EmbryoStraw => ({
  embryoStrawId: straw.onFarmEmbryoStrawId,
  embryoStrawCode: straw.embryoStrawCode ?? '',
  embryoStrawNumber: straw.embryoStrawNumber,
  embryoGrade: straw.embryoGrade ?? undefined,
  embryoStage: straw.embryoStage ?? undefined,
  embryoCountPerStraw: straw.embryoCountPerStraw ?? 1,
  storageCanisterId: straw.accountStorageCanisterId ?? canisterId,
  specimenId: straw.onFarmSpecimenId ?? onFarmSpecimenId,
});

const mapOnFarmLocationToSpecimenInventory = (location: OnFarmSpecimenLocation, canisterId: number): SpecimenInventory => {
  const specimen = location.onFarmSpecimen;
  const animal = specimen?.onFarmAnimal;
  const onFarmAnimalId = animal?.onFarmAnimalId ?? animal?.animalId ?? specimen?.onFarmAnimalId ?? 0;
  const availableStrawIds = new Set(location.selectedEmbryoStrawIds ?? []);
  const mappedStraws = (specimen?.onFarmEmbryoStraws ?? [])
    .map(straw => mapOnFarmEmbryoStraw(straw, location.onFarmSpecimenId, canisterId))
    .filter(straw => availableStrawIds.size === 0 || availableStrawIds.has(straw.embryoStrawId));

  const quantityAvailable = location.quantity ?? 0;
  const qualityValue = (specimen?.quality as QualityType) || QualityType.Unknown;
  const uomValue = (specimen?.specimenUOM as specimenUOM) || specimenUOM.Default;

  return {
    specimenId: location.onFarmSpecimenId,
    animalId: onFarmAnimalId,
    animal: {
      animalId: onFarmAnimalId,
      name: animal?.name ?? '',
      code: animal?.code ?? '',
      gender: animal?.gender ?? Genders.Default,
    },
    collectedBy: specimen?.collectedBy ?? 0,
    freezeDate: specimen?.freezeDate ? new Date(specimen.freezeDate) : new Date(),
    frozenBy: specimen?.frozenBy ?? 0,
    originalQuantity: specimen?.originalQuantity ?? quantityAvailable,
    processedBy: specimen?.processedBy ?? 0,
    quality: qualityValue,
    qualityPercentage: specimen?.qualityPercentage ?? 0,
    specimenTypeId: specimen?.specimenTypeId ?? specimen?.specimenType?.specimenTypeId ?? 0,
    specimenUOM: uomValue,
    lotNumber: specimen?.lotNumber,
    notes: specimen?.notes ?? undefined,
    specimenCustomDataValues: [],
    specimenType: specimen?.specimenType
      ? {
          specimenTypeId: specimen.specimenType.specimenTypeId,
          name: specimen.specimenType.name,
          active: specimen.specimenType.active ?? true,
        }
      : undefined,
    quantityAvailable,
    quantityOwned: quantityAvailable,
    embryoStrawId: 0,
    embryoCountPerStraw: 0,
    embryoStraws: mappedStraws,
    specimenLocations: [
      {
        storageCanisterId: location.accountStorageCanisterId || canisterId,
        quantity: quantityAvailable,
        specimenId: location.onFarmSpecimenId,
        transactionLinkId: location.transactionLinkId ?? 0,
      },
    ],
  };
};

export const getOnFarmTotalUnits = async (_accountId?: number): Promise<AxiosResponse<number>> => {
  const response = await onFarmSpecimenApi.getOnFarmSpecimens({
    include: 'OnFarmSpecimenLocations,OnFarmEmbryoStraws',
    limit: 0,
  });
  const specimens = Array.isArray(response.data) ? response.data : response.data?.result ?? [];
  const totalUnits = specimens.reduce((total, specimen) => {
    const locationsQuantity = (specimen.onFarmSpecimenLocations ?? []).reduce(
      (quantity, location) => quantity + (location.quantity ?? 0),
      0,
    );
    return total + locationsQuantity;
  }, 0);

  return { ...response, data: totalUnits };
};

export const getOnFarmInventoryGraphicsData = async (
  accountId: number,
  activeContext = activeContexts.account,
): Promise<InventoryGraphicsData> => {
  const params = {
    include: 'OnFarmAnimal,SpecimenType,OnFarmSpecimenLocations',
    limit: 0,
  };
  const response =
    activeContext === activeContexts.app
      ? await onFarmSpecimenApi.getOnFarmSpecimensByAccountId(accountId, params)
      : await onFarmSpecimenApi.getOnFarmSpecimens(params);
  const specimens = Array.isArray(response.data) ? response.data : response.data?.result ?? [];
  const typeQuantities = new Map<number, { typeName?: string; typeId: number; quantity: number }>();
  const animalQuantities = new Map<number, { quantity: number; animal: Animal }>();
  let totalInventory = 0;

  specimens.forEach(specimen => {
    const quantity = (specimen.onFarmSpecimenLocations ?? []).reduce((sum, location) => sum + (location.quantity ?? 0), 0);
    if (quantity <= 0) {
      return;
    }

    totalInventory += quantity;

    const typeId = specimen.specimenTypeId ?? specimen.specimenType?.specimenTypeId;
    if (typeId) {
      const currentType = typeQuantities.get(typeId);
      typeQuantities.set(typeId, {
        typeId,
        typeName: specimen.specimenType?.name ?? currentType?.typeName,
        quantity: (currentType?.quantity ?? 0) + quantity,
      });
    }

    const animal = specimen.onFarmAnimal;
    const animalId = specimen.onFarmAnimalId ?? animal?.onFarmAnimalId ?? animal?.animalId;
    if (animal && animalId) {
      const currentAnimal = animalQuantities.get(animalId);
      animalQuantities.set(animalId, {
        animal: currentAnimal?.animal ?? mapOnFarmAnimal(animal),
        quantity: (currentAnimal?.quantity ?? 0) + quantity,
      });
    }
  });

  return {
    totalInventory,
    typeInventory: Array.from(typeQuantities.values()),
    animalInventory: Array.from(animalQuantities.values()),
  };
};
